import { when } from '@execonline-inc/maybe-adapter';
import { InvalidUrlError } from '@execonline-inc/url';
import { HttpError } from 'ajaxian';
import { Maybe, nothing } from 'maybeasy';
import { fromArrayMaybe } from 'nonempty-list';
import { AppyError, MissingApiCompatibility, MissingApplicationId } from '../../../../Appy';
import { Error } from '../../../../ErrorHandling';
import { MissingLinkError } from '../../../../LinkyLinky';
import { FormTemplate, HasFormTemplates } from '../../../../Resource/FormTemplates/Types';
import { Resource, ValidationError } from '../../../../Resource/Types';
import {
  ExperienceResources,
  LearningDesignResource,
  OfferingType,
  ProductCollectionResource,
  ProductLicenseResource,
} from '../../Types';
import LearningDesignExperienceStore from '../Experience/Store';
import { experienceStore } from '../Experience/Types';
import { FileMetaData } from '../Edit/CurrentCollectionStep/Details/S3ImageUpload/Types';
import { ShowUnlicensedToggle } from '../CurrentCollectionStep/Builder/ExperienceCatalog/Types';

export interface CompetencyFilterOptionData {
  id: number;
  name: string;
  count: number;
}

export interface ProductTypeFilterOptionData {
  id: number;
  name: OfferingType;
  count: number;
}

export type BrowseCatalog = 'displayed' | 'hidden';
export interface LearningCollectionNewFormPayload {
  kind: 'new';
  availableExtensionIds: number[];
  allExperiences: ExperienceResources;
  browseCatalog: BrowseCatalog;
  warningStatus: ShowUnlicensedToggle;
}

export interface ReadyArgs {
  resource: LearningCollectionNewFormResource;
  template: FormTemplate;
}

export interface Templates {
  template: FormTemplate;
  publishTemplate: FormTemplate;
}

export type LearningCollectionNewFormResource = Resource<LearningCollectionNewFormPayload> &
  HasFormTemplates;

export interface MissingTemplates {
  kind: 'missing-templates';
}

export const missingTemplates: MissingTemplates = { kind: 'missing-templates' };

export type LearningCollectionError =
  | HttpError
  | MissingTemplates
  | MissingLinkError
  | MissingApplicationId
  | MissingApiCompatibility
  | AppyError
  | Error
  | ValidationError
  | InvalidUrlError;

export type LearningDesignState =
  | 'empty'
  | 'curated'
  | 'curated-custom'
  | 'custom'
  | 'blank'
  | 'build-your-own'
  | 'browse-all';

export type BrowseAllContext = { kind: 'browse-all-experiences' };

export function browseAllContext(): BrowseAllContext {
  return { kind: 'browse-all-experiences' };
}

export type SectionContext = { kind: 'section-context'; section: Section };

export function sectionContext(section: Section): SectionContext {
  return { kind: 'section-context', section };
}

export type ModifiableExperiencesContext = BrowseAllContext | SectionContext;

export interface ModifiableExperiences {
  experienceStores: ReadonlyArray<LearningDesignExperienceStore>;
  deletedExperienceStores: ReadonlyArray<LearningDesignExperienceStore>;
}

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
}

export interface LoadError {
  kind: 'load-error';
  error: LearningCollectionError;
}

interface BaseSection {
  template: FormTemplate;
  learningDesignExperiences: ModifiableExperiences;
}

export interface LearningDesignSection extends BaseSection {
  kind: 'learning-design-section';
  resource: LearningDesignResource;
}

export interface ProductCollectionSection extends BaseSection {
  kind: 'product-collection-section';
  resource: ProductCollectionResource;
}

export type Section = LearningDesignSection | ProductCollectionSection;

export interface Ready {
  kind: 'ready';
  learningCollectionFormResource: LearningCollectionNewFormResource;
  template: FormTemplate;
}

export interface CommonState {
  template: FormTemplate;
  selectedProductLicense: Maybe<ProductLicenseResource>;
  browseAllExperiences: ModifiableExperiences;
  sections: ReadonlyArray<never>; // For compatibility with LearningCollectionEditStore
}

export interface ChoosingProductLicense extends CommonState {
  kind: 'choosing-product-license';
  initialProductLicense: Maybe<ProductLicenseResource>;
  learningCollectionFormResource: LearningCollectionNewFormResource;
}

export interface ReloadAllExperiences extends CommonState {
  kind: 'reload-all-experiences';
  initialProductLicense: Maybe<ProductLicenseResource>;
  learningCollectionFormResource: LearningCollectionNewFormResource;
}

export interface Building extends CommonState {
  kind: 'building';
  learningCollectionFormResource: LearningCollectionNewFormResource;
}

export interface Creating extends CommonState {
  kind: 'creating';
  learningCollectionFormResource: LearningCollectionNewFormResource;
  fileData: Maybe<FileMetaData>;
}

export interface NewFormSubmissionError extends CommonState {
  kind: 'new-form-submission-error';
  learningCollectionFormResource: LearningCollectionNewFormResource;
  error: LearningCollectionError;
}

export type LearningCollectionState =
  | Waiting
  | Loading
  | Ready
  | Building
  | Creating
  | LoadError
  | NewFormSubmissionError
  | ChoosingProductLicense
  | ReloadAllExperiences;

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (): Loading => ({
  kind: 'loading',
});

export const loadError = (error: LearningCollectionError): LoadError => ({
  kind: 'load-error',
  error,
});

export const ready = (
  learningCollectionFormResource: LearningCollectionNewFormResource,
  template: FormTemplate,
): Ready => {
  return {
    kind: 'ready',
    learningCollectionFormResource,
    template,
  };
};

export const reloadAllExperiences = (state: ChoosingProductLicense): ReloadAllExperiences => {
  const { kind, ...previous } = state;

  return {
    kind: 'reload-all-experiences',
    ...previous,
  };
};

export const building = (
  state: ReloadAllExperiences,
  learningCollectionNewFormResource: LearningCollectionNewFormResource,
): Building => {
  const { kind, ...previous } = state;
  return {
    kind: 'building',
    ...previous,
    learningCollectionFormResource: learningCollectionNewFormResource,
  };
};

export const choosingProductLicense = (state: Ready | Building): ChoosingProductLicense => {
  const staticValues: Pick<
    ChoosingProductLicense,
    'kind' | 'learningCollectionFormResource' | 'template'
  > = {
    kind: 'choosing-product-license',
    learningCollectionFormResource: state.learningCollectionFormResource,
    template: state.template,
  };

  switch (state.kind) {
    case 'ready':
      return {
        ...staticValues,
        initialProductLicense: nothing(),
        selectedProductLicense: nothing(),
        browseAllExperiences: {
          experienceStores:
            state.learningCollectionFormResource.payload.allExperiences.map(experienceStore),
          deletedExperienceStores: [],
        },
        sections: [],
      };
    case 'building':
      return {
        ...staticValues,
        initialProductLicense: state.selectedProductLicense,
        selectedProductLicense: state.selectedProductLicense,
        browseAllExperiences: state.browseAllExperiences,
        sections: state.sections,
      };
  }
};

export const creating = (
  learningCollectionNewFormResource: LearningCollectionNewFormResource,
  state: Building | NewFormSubmissionError,
  fileData: Maybe<FileMetaData>,
): Creating => {
  const { kind, ...previous } = state;

  return {
    kind: 'creating',
    ...previous,
    fileData: fileData,
    learningCollectionFormResource: learningCollectionNewFormResource,
  };
};

export const newFormSubmissionError = (
  learningCollectionNewFormResource: LearningCollectionNewFormResource,
  template: FormTemplate,
  error: LearningCollectionError,
  state: Creating,
): NewFormSubmissionError => {
  const { kind, ...previous } = state;
  return {
    kind: 'new-form-submission-error',
    ...previous,
    learningCollectionFormResource: learningCollectionNewFormResource,
    template,
    error,
  };
};

const asConfiguredLicense = (productLicense: ProductLicenseResource) =>
  productLicense.payload.title.text === 'As Configured';

export const onlyAsConfiguredActiveLicense = (activeLicenses: Array<ProductLicenseResource>) => {
  return fromArrayMaybe(activeLicenses).andThen((activeLicenses) =>
    when(activeLicenses.length === 1 && asConfiguredLicense(activeLicenses.first), activeLicenses),
  );
};

export function isSameSection(a: Section, b: Section): boolean;
export function isSameSection(a: Section): (b: Section) => boolean;
export function isSameSection(a: Section, b?: Section) {
  const doit = (other: Section): boolean => a.resource.payload.id === other.resource.payload.id;
  return typeof b === 'undefined' ? doit : doit(b);
}
